import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { salesApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

type Tab = 'statement' | 'history' | 'communications';

/**
 * FR-SALES-03.04/05/06 — Customer detail with statement/history/comms tabs.
 */
export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('statement');

  const custQuery = useQuery({
    queryKey: ['customer', id],
    queryFn: () => salesApi.getCustomer(id!),
    enabled: !!id,
  });

  if (custQuery.isLoading) return <LoadingState />;
  if (custQuery.error) return <ErrorState error={custQuery.error} />;
  const c = custQuery.data;

  return (
    <div className="p-6">
      <PageHeader
        title={c.name}
        subtitle={`${c.code} · ${c.type} · ${c.email ?? 'no email'}`}
        actions={<Link to="/sales/customers" className="btn-secondary">← Back to list</Link>}
      />

      <div className="card p-4 mb-4">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-slate-500">Status</div>
            <div className="font-semibold">{c.status}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Credit limit</div>
            <div className="font-mono">{fmtCurrency(parseFloat(c.credit_limit ?? 0))}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Loyalty points</div>
            <div>{c.loyalty_points ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Segments</div>
            <div className="flex flex-wrap gap-1">
              {(c.segments ?? []).length === 0 ? <span className="text-slate-400 text-xs">—</span> :
                c.segments.map((s: string) => (
                  <span key={s} className="badge bg-sage-100 text-sage-700 text-2xs">{s}</span>
                ))}
            </div>
          </div>
        </div>
        {c.rfm_score && (
          <div className="text-xs text-slate-500 mt-2">
            RFM: <span className="font-mono">{c.rfm_score}</span>
            {' '}(R:{c.recency_score} F:{c.frequency_score} M:{c.monetary_score})
            {c.last_segmented_at && ` · last scored ${new Date(c.last_segmented_at).toLocaleDateString()}`}
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {([
          ['statement', 'Statement'],
          ['history', 'Purchase History'],
          ['communications', 'Communications'],
        ] as [Tab, string][]).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${
              tab === k ? 'border-sage-500 text-sage-600 font-semibold'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>{label}</button>
        ))}
      </div>

      {tab === 'statement' && <StatementTab customerId={id!} />}
      {tab === 'history' && <HistoryTab customerId={id!} />}
      {tab === 'communications' && <CommunicationsTab customerId={id!} />}
    </div>
  );
}

function StatementTab({ customerId }: { customerId: string }) {
  const today = new Date().toISOString().slice(0, 10);
  const threeMonthsAgo = (() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().slice(0, 10); })();
  const [fromDate, setFromDate] = useState(threeMonthsAgo);
  const [toDate, setToDate] = useState(today);

  const q = useQuery({
    queryKey: ['customer-statement', customerId, fromDate, toDate],
    queryFn: () => salesApi.getCustomerStatement(customerId, { fromDate, toDate }),
  });

  return (
    <div>
      <div className="card p-4 mb-4 flex gap-3 items-end">
        <div>
          <label className="label">From</label>
          <input type="date" className="input w-44" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input w-44" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <>
          <div className="card p-4 mb-4">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div><div className="text-xs text-slate-500">Invoiced</div><div className="font-mono">{fmtCurrency(q.data.totalInvoiced)}</div></div>
              <div><div className="text-xs text-slate-500">Paid</div><div className="font-mono text-emerald-700">{fmtCurrency(q.data.totalPaid)}</div></div>
              <div><div className="text-xs text-slate-500">Credits</div><div className="font-mono text-amber-600">{fmtCurrency(q.data.totalCredits)}</div></div>
              <div><div className="text-xs text-slate-500 font-semibold">Outstanding</div><div className="font-mono text-lg font-bold">{fmtCurrency(q.data.currentBalance)}</div></div>
            </div>
          </div>

          <div className="card overflow-x-auto">
            <table className="table text-xs">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Document</th>
                  <th>Description</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Credit</th>
                  <th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(q.data?.events?.length ?? 0) === 0 ? (
                  <tr><td colSpan={7}><EmptyState message="No AR activity in this period." /></td></tr>
                ) : (q.data?.events ?? []).map((e: any, i: number) => (
                  <tr key={i}>
                    <td>{new Date(e.date).toLocaleDateString()}</td>
                    <td className="text-xs">{e.type.replace('_', ' ')}</td>
                    <td className="font-mono text-2xs">{e.documentNumber}</td>
                    <td className="text-xs">{e.description}</td>
                    <td className="text-right font-mono">{e.debit > 0 ? `${fmtCurrency(e.debit)}` : ''}</td>
                    <td className="text-right font-mono text-emerald-700">{e.credit > 0 ? `${fmtCurrency(e.credit)}` : ''}</td>
                    <td className="text-right font-mono font-semibold">{fmtCurrency(e.runningBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function HistoryTab({ customerId }: { customerId: string }) {
  const q = useQuery({
    queryKey: ['customer-history', customerId],
    queryFn: () => salesApi.getCustomerPurchaseHistory(customerId, 100),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const h = q.data;

  return (
    <div>
      <div className="card p-4 mb-4">
        <div className="grid grid-cols-4 gap-2 text-center">
          <div><div className="text-xs text-slate-500">Orders</div><div className="font-semibold">{h.summary.totalOrders}</div></div>
          <div><div className="text-xs text-slate-500">Total spend</div><div className="font-mono">{fmtCurrency(h.summary.totalSpend)}</div></div>
          <div><div className="text-xs text-slate-500">Avg order</div><div className="font-mono">{fmtCurrency(h.summary.avgOrderValue)}</div></div>
          <div>
            <div className="text-xs text-slate-500">First → last</div>
            <div className="text-xs">
              {h.summary.firstOrder ? new Date(h.summary.firstOrder).toLocaleDateString() : '—'}
              {' → '}
              {h.summary.lastOrder ? new Date(h.summary.lastOrder).toLocaleDateString() : '—'}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-x-auto">
          <h3 className="font-semibold text-sm p-3 border-b">Orders</h3>
          <table className="table text-xs">
            <thead><tr><th>Order #</th><th>Date</th><th>Status</th><th className="text-right">Total</th></tr></thead>
            <tbody>
              {h.orders.length === 0 ? (
                <tr><td colSpan={4}><EmptyState message="No orders." /></td></tr>
              ) : h.orders.map((o: any) => (
                <tr key={o.id}>
                  <td className="font-mono">{o.order_number}</td>
                  <td>{new Date(o.order_date).toLocaleDateString()}</td>
                  <td>{o.status}</td>
                  <td className="text-right font-mono">{fmtCurrency(parseFloat(o.grand_total))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card overflow-x-auto">
          <h3 className="font-semibold text-sm p-3 border-b">Top products</h3>
          <table className="table text-xs">
            <thead><tr><th>SKU</th><th>Name</th><th className="text-right">Qty</th><th className="text-right">Revenue</th></tr></thead>
            <tbody>
              {h.topProducts.length === 0 ? (
                <tr><td colSpan={4}><EmptyState message="No products purchased." /></td></tr>
              ) : h.topProducts.map((p: any) => (
                <tr key={p.productId}>
                  <td className="font-mono text-2xs">{p.sku}</td>
                  <td className="text-xs">{p.name}</td>
                  <td className="text-right">{p.totalQty.toFixed(0)}</td>
                  <td className="text-right font-mono">{fmtCurrency(p.totalRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CommunicationsTab({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const [showLog, setShowLog] = useState(false);

  const listQuery = useQuery({
    queryKey: ['customer-comms', customerId],
    queryFn: () => salesApi.listCustomerCommunications(customerId, 50),
  });

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button className="btn-primary" onClick={() => setShowLog(true)}>+ Log communication</button>
      </div>

      {listQuery.isLoading ? <LoadingState /> : listQuery.error ? <ErrorState error={listQuery.error} /> : (
        <div className="card">
          {!listQuery.data?.length ? <EmptyState message="No communications logged." /> : (
            <ul className="divide-y divide-slate-200">
              {(listQuery.data ?? []).map((c: any) => (
                <li key={c.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="badge bg-canvas-subtle text-ink-700 ring-ink-200 text-2xs mr-2">{c.channel}</span>
                      {c.direction && <span className="text-xs text-slate-500">{c.direction === 'in' ? '← inbound' : '→ outbound'}</span>}
                      {c.subject && <span className="font-semibold text-sm ml-2">{c.subject}</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(c.created_at).toLocaleString()}
                      {c.created_by_name && ` · ${c.created_by_name}`}
                    </div>
                  </div>
                  {c.body && <div className="text-sm text-slate-600 mt-1 whitespace-pre-line">{c.body}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showLog && <LogCommunicationModal customerId={customerId}
        onClose={() => setShowLog(false)}
        onLogged={() => { qc.invalidateQueries({ queryKey: ['customer-comms', customerId] }); setShowLog(false); }} />}
    </div>
  );
}

function LogCommunicationModal({ customerId, onClose, onLogged }:
    { customerId: string; onClose: () => void; onLogged: () => void }) {
  const [channel, setChannel] = useState<'email'|'sms'|'call'|'whatsapp'|'note'>('note');
  const [direction, setDirection] = useState<'in'|'out'|''>('out');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const mut = useMutation({
    mutationFn: () => salesApi.logCustomerCommunication(customerId, {
      channel,
      direction: direction || undefined,
      subject: subject || undefined,
      body: body || undefined,
    }),
    onSuccess: () => { toast.success('Logged'); onLogged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Sales · Customer</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Log communication</h2>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Channel</label>
            <select className="input" value={channel} onChange={(e) => setChannel(e.target.value as any)}>
              <option value="note">Note</option>
              <option value="email">Email</option>
              <option value="call">Call</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <div>
            <label className="label">Direction</label>
            <select className="input" value={direction} onChange={(e) => setDirection(e.target.value as any)}>
              <option value="">—</option>
              <option value="in">Inbound</option>
              <option value="out">Outbound</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">Subject <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <label className="label">Body / notes</label>
          <textarea className="input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Logging…' : 'Log communication'}
        </button>
      </div>
    </Modal>
  );
}
